// /admin/billing-reconciliation — month by month (Brisbane calendar), does our
// ledger agree with Stripe? Totals, every disagreement with a one-click fix
// where one is safe, and the month's refunds and disputes.
//
// Data: GET /api/admin/billing-reconciliation?month=YYYY-MM (built live from
// Stripe; services/billing-reconciliation.ts). Fixes POST to /fix.

import { Alert, Badge, Button, Group, Loader, Select, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { EmptyState } from '../../components/ui';
import { StatCard } from '../../components/ui/StatCard';
import { showToast } from '../../lib/toast';
import { adminGet, adminPost } from './_api';

type IssueKind = 'missing_in_ledger' | 'amount_mismatch' | 'paid_month_differs' | 'not_in_stripe' | 'reversal_not_applied';

type Report = {
  month: string;
  generatedAt: string;
  currency: string;
  totals: {
    stripePaidMinor: number;
    stripePaidCount: number;
    ledgerPaidMinor: number;
    ledgerPaidCount: number;
    refundedMinor: number;
    disputedMinor: number;
    netCollectedMinor: number;
    otherAccountsCount: number;
    referralEarnedMinor: number;
    referralStatementsPaidMinor: number;
    referralStatementsUnpaidMinor: number;
  };
  matchedCount: number;
  issues: Array<{
    kind: IssueKind;
    invoiceId: string;
    venueName: string | null;
    stripeMinor: number | null;
    ledgerMinor: number | null;
    at: string | null;
    detail: string;
    full?: boolean;
    label?: string;
  }>;
  reversals: Array<{
    id: string;
    kind: 'refund' | 'dispute';
    amountMinor: number;
    currency: string;
    createdAt: number;
    invoiceId: string | null;
    full: boolean;
    venueName: string | null;
    applied: boolean | null;
  }>;
  snapshot: { checkedAt: string; issueCount: number } | null;
};

const ISSUE_COPY: Record<IssueKind, { label: string; fix: string | null }> = {
  missing_in_ledger: { label: 'Not in our ledger', fix: 'Record it' },
  paid_month_differs: { label: 'Wrong month', fix: "Use Stripe's date" },
  reversal_not_applied: { label: 'Refund not applied', fix: 'Apply to payout' },
  amount_mismatch: { label: 'Amounts differ', fix: null },
  not_in_stripe: { label: 'Not paid in Stripe', fix: null },
};

export const Route = createFileRoute('/admin/billing-reconciliation')({
  component: AdminBillingReconciliation,
});

function money(minor: number | null, currency = 'AUD'): string {
  if (minor === null) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(minor / 100);
}

function day(iso: string | number | null): string {
  if (iso === null) return '—';
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Brisbane' }).format(
    new Date(iso),
  );
}

/** The current Brisbane month and the 11 before it, newest first. */
function recentMonths(): Array<{ value: string; label: string }> {
  const local = new Date(Date.now() + 10 * 60 * 60 * 1000);
  const out: Array<{ value: string; label: string }> = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth() - i, 1));
    out.push({
      value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d),
    });
  }
  return out;
}

function AdminBillingReconciliation() {
  const qc = useQueryClient();
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[0]!.value);

  const report = useQuery({
    queryKey: ['admin', 'billing-reconciliation', month],
    queryFn: () => adminGet<Report>(`/api/admin/billing-reconciliation?month=${month}`),
  });

  const fix = useMutation({
    mutationFn: (issue: Report['issues'][number]) =>
      adminPost<{ ok: true; outcome: string }>('/api/admin/billing-reconciliation/fix', {
        kind: issue.kind,
        invoiceId: issue.invoiceId,
        ...(issue.full !== undefined ? { full: issue.full } : {}),
        ...(issue.label !== undefined ? { label: issue.label } : {}),
      }),
    onSuccess: async () => {
      showToast({ title: 'Fixed', body: 'The ledger now matches Stripe for that invoice.', kind: 'success' });
      await qc.invalidateQueries({ queryKey: ['admin', 'billing-reconciliation'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'referral-payouts'] });
    },
    onError: (err: Error) => showToast({ title: 'Could not fix', body: err.message, kind: 'error' }),
  });

  const r = report.data;
  const cur = r?.currency ?? 'AUD';
  const monthLabel = months.find((m) => m.value === month)?.label ?? month;

  return (
    <AdminShell>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Stack gap={4}>
            <Title order={2}>Billing reconciliation</Title>
            <Text c="dimmed" size="sm" maw={720}>
              Checks our ledger against Stripe for one calendar month (Brisbane time): every venue payment, refund and
              dispute, and what referrers earned. Last month is checked automatically on the 2nd.
            </Text>
          </Stack>
          <Select
            label="Month"
            data={months}
            value={month}
            onChange={(v) => v !== null && setMonth(v)}
            allowDeselect={false}
            w={200}
          />
        </Group>

        {report.isLoading ? (
          <Loader />
        ) : report.isError ? (
          <Alert color="red" title="Couldn't build the report">
            {report.error.message}
          </Alert>
        ) : r === undefined ? null : (
          <>
            {r.snapshot !== null && (
              <Text size="sm" c="dimmed">
                Checked automatically on {day(r.snapshot.checkedAt)}:{' '}
                {r.snapshot.issueCount === 0 ? 'no issues' : `${r.snapshot.issueCount} issue${r.snapshot.issueCount === 1 ? '' : 's'}`}.
              </Text>
            )}

            <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
              <StatCard
                label="Stripe collected"
                value={money(r.totals.stripePaidMinor, cur)}
                hint={`${r.totals.stripePaidCount} venue payment${r.totals.stripePaidCount === 1 ? '' : 's'}`}
              />
              <StatCard
                label="Our ledger"
                value={money(r.totals.ledgerPaidMinor, cur)}
                hint={`${r.totals.ledgerPaidCount} recorded · ${r.matchedCount} match Stripe`}
                emphasis={r.totals.ledgerPaidMinor === r.totals.stripePaidMinor ? 'brand' : 'sun'}
              />
              <StatCard
                label="Net collected"
                value={money(r.totals.netCollectedMinor, cur)}
                hint={`${money(r.totals.refundedMinor, cur)} refunded · ${money(r.totals.disputedMinor, cur)} disputed`}
                emphasis="ink"
              />
              <StatCard
                label="Referrers earned"
                value={money(r.totals.referralEarnedMinor, cur)}
                hint="10% of ex-GST venue payments"
                emphasis="ink"
              />
              <StatCard
                label="Referrers paid for this month"
                value={money(r.totals.referralStatementsPaidMinor, cur)}
                hint={
                  r.totals.referralStatementsUnpaidMinor > 0
                    ? `${money(r.totals.referralStatementsUnpaidMinor, cur)} still sending`
                    : 'Monthly statements, paid on the 7th'
                }
                emphasis="ink"
              />
              <StatCard
                label="Issues"
                value={String(r.issues.length)}
                hint={r.issues.length === 0 ? 'Ledger matches Stripe' : 'See below'}
                emphasis={r.issues.length === 0 ? 'brand' : 'sun'}
              />
            </SimpleGrid>

            <Stack gap="sm">
              <Title order={4}>Issues</Title>
              {r.issues.length === 0 ? (
                <EmptyState title={`${monthLabel} reconciles`} body="Every Stripe payment, refund and dispute matches our ledger." />
              ) : (
                <Table striped verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Venue</Table.Th>
                      <Table.Th>Issue</Table.Th>
                      <Table.Th ta="right">Stripe</Table.Th>
                      <Table.Th ta="right">Ledger</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {r.issues.map((i) => (
                      <Table.Tr key={`${i.kind}-${i.invoiceId}`}>
                        <Table.Td>{day(i.at)}</Table.Td>
                        <Table.Td>{i.venueName ?? '—'}</Table.Td>
                        <Table.Td>
                          <Badge color="orange" variant="light" size="sm">
                            {ISSUE_COPY[i.kind].label}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {i.detail} <Text span ff="monospace" size="xs">{i.invoiceId}</Text>
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">{money(i.stripeMinor, cur)}</Table.Td>
                        <Table.Td ta="right">{money(i.ledgerMinor, cur)}</Table.Td>
                        <Table.Td align="right">
                          {ISSUE_COPY[i.kind].fix !== null ? (
                            <Button
                              size="xs"
                              variant="light"
                              loading={fix.isPending && fix.variables?.invoiceId === i.invoiceId}
                              onClick={() => fix.mutate(i)}
                            >
                              {ISSUE_COPY[i.kind].fix}
                            </Button>
                          ) : (
                            <Text size="xs" c="dimmed">
                              Check in Stripe
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>

            <Stack gap="sm">
              <Title order={4}>Refunds and disputes</Title>
              {r.reversals.length === 0 ? (
                <Text size="sm" c="dimmed">
                  None in {monthLabel}.
                </Text>
              ) : (
                <Table striped verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Venue</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th ta="right">Amount</Table.Th>
                      <Table.Th>Referral payout</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {r.reversals.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td>{day(v.createdAt)}</Table.Td>
                        <Table.Td>{v.venueName ?? '—'}</Table.Td>
                        <Table.Td>{v.kind === 'dispute' ? 'Dispute' : v.full ? 'Full refund' : 'Partial refund'}</Table.Td>
                        <Table.Td ta="right">{money(v.amountMinor, v.currency)}</Table.Td>
                        <Table.Td>
                          {v.applied === null ? (
                            <Text size="sm" c="dimmed">
                              None on this payment
                            </Text>
                          ) : (
                            <Badge color={v.applied ? 'brand' : 'orange'} variant="light" size="sm">
                              {v.applied ? 'Handled' : 'Not applied'}
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>

            {r.totals.otherAccountsCount > 0 && (
              <Text size="xs" c="dimmed">
                {r.totals.otherAccountsCount === 1
                  ? `1 Stripe payment in ${monthLabel} belongs to a subscription this site doesn't hold (e.g. another test site sharing the Stripe Sandbox) and is left out.`
                  : `${r.totals.otherAccountsCount} Stripe payments in ${monthLabel} belong to subscriptions this site doesn't hold (e.g. other test sites sharing the Stripe Sandbox) and are left out.`}
              </Text>
            )}
          </>
        )}
      </Stack>
    </AdminShell>
  );
}
